/**
 * RecallAlertBell — signed-in user notification bell for product recalls
 * matching items they have scanned.
 *
 * Polls `GET /api/recall-alerts/mine?unread_only=true` every 60 seconds
 * (and immediately on mount). When there is at least one unread alert
 * we:
 *   1. Show an AlertTriangle badge with the count on the bell icon.
 *   2. Toast the freshest match once per session so the user notices
 *      even if they never open the bell.
 *   3. Open a dropdown listing every match with a link back to the
 *      matched product page + a "Mark all as read" action.
 */
import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { AlertTriangle, X } from "lucide-react";
import { toast } from "sonner";
import { http } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";

const RED = "#B91C1C";
const CREAM = "#FBF7EF";

export default function RecallAlertBell() {
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [unread, setUnread] = useState(0);
  const [open, setOpen] = useState(false);
  const toastedRef = useRef(new Set());   // recall_ids toasted this session

  const load = async () => {
    if (!user) return;
    try {
      const r = await http.get("/recall-alerts/mine", { params: { limit: 15 } });
      setItems(r.data.items || []);
      setUnread(r.data.unread || 0);
      // Toast the freshest unread — once per session per recall_id.
      const fresh = (r.data.items || []).find((it) => !it.read && !toastedRef.current.has(it.recall_id));
      if (fresh) {
        toastedRef.current.add(fresh.recall_id);
        toast.warning(`Recall alert: ${fresh.product_name || "a product you scanned"}`, {
          description: fresh.recall_title,
          duration: 8000,
        });
      }
    } catch { /* silent */ }
  };

  useEffect(() => {
    if (!user) return;
    load();
    const int = setInterval(load, 60_000);
    return () => clearInterval(int);
    // eslint-disable-next-line
  }, [user?.id]);

  const markAllRead = async () => {
    try { await http.post("/recall-alerts/mine/mark-read", {}); } catch { /* noop */ }
    setUnread(0);
    setItems((prev) => prev.map((x) => ({ ...x, read: true })));
  };

  if (!user) return null;

  return (
    <div className="relative" data-testid="recall-alert-bell">
      <button
        onClick={() => setOpen(!open)}
        className="relative rounded-full p-2 hover:bg-black/5 transition"
        title="Recall alerts on products you've scanned"
        data-testid="recall-bell-btn"
      >
        <AlertTriangle size={18} style={{ color: unread > 0 ? RED : "#78716C" }} />
        {unread > 0 && (
          <span
            className="absolute -top-1 -right-1 text-[9px] font-black rounded-full w-4 h-4 flex items-center justify-center"
            style={{ background: RED, color: "white" }}
            data-testid="recall-unread-badge"
          >
            {unread > 9 ? "9+" : unread}
          </span>
        )}
      </button>

      {open && (
        <div
          className="absolute right-0 mt-2 w-80 max-h-96 overflow-y-auto rounded-2xl border bg-white shadow-lg z-40"
          style={{ borderColor: "#E9D8B4" }}
          data-testid="recall-alerts-dropdown"
        >
          <header className="flex items-center justify-between p-3 border-b sticky top-0 bg-white z-10" style={{ borderColor: "#F5E6C4" }}>
            <div className="text-sm font-bold" style={{ color: RED }}>Recall Alerts</div>
            <div className="flex items-center gap-2">
              {unread > 0 && (
                <button onClick={markAllRead} className="text-[11px] font-semibold hover:underline" style={{ color: "#7A3E00" }} data-testid="mark-all-read">
                  Mark all read
                </button>
              )}
              <button onClick={() => setOpen(false)} className="opacity-60 hover:opacity-100"><X size={14} /></button>
            </div>
          </header>

          {items.length === 0 ? (
            <div className="p-6 text-center text-sm text-gray-600" data-testid="recall-alerts-empty">
              No recall alerts on products you've scanned.
            </div>
          ) : (
            <ul className="divide-y" style={{ borderColor: "#F5E6C4" }}>
              {items.map((it) => (
                <li key={`${it.recall_id}:${it.product_id}`} className={`p-3 ${it.read ? "opacity-70" : ""}`} data-testid={`recall-alert-${it.recall_id}`}>
                  <Link to={`/food-pulse/products/${it.product_id}`} onClick={() => setOpen(false)}>
                    <div className="text-xs font-bold" style={{ color: RED }}>{it.product_name}</div>
                    <div className="text-xs text-gray-700 mt-0.5 line-clamp-2">{it.recall_title}</div>
                    <div className="text-[10px] mt-1 opacity-70 font-mono">{it.created_at?.slice(0, 10)}</div>
                  </Link>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
    </div>
  );
}
